'use client';

import { AnimationContainer } from '@/components/ui/animation-container';
import { MagicBadge } from '@/components/ui/magic-badge';
import { WobbleCard } from '@/components/ui/wobble-card';
import { Award, ArrowRight, CheckCircle } from 'lucide-react';
import { TranslationKeys } from '../types';

export default function BuiltForSection({ t }: { t: TranslationKeys }) {
  return (
    <section id="built-for" className="pt-6 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        <AnimationContainer delay={0.1}>
          <div className="flex flex-col items-center justify-center w-full py-4 max-w-xl mx-auto mb-12">
            <MagicBadge title={t.builtForBadge} />
            <h2 className="text-center text-3xl md:text-5xl !leading-[1.1] font-bold text-gray-900 mt-6">
              {t.builtForTitle}
            </h2>
            <p className="mt-4 text-center text-lg text-gray-600 max-w-lg">
              {t.builtForDesc}
            </p>
          </div>
        </AnimationContainer>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
          {/* Card 1 - Wide: Certified Translators with dashboard mockup */}
          <WobbleCard
            containerClassName="col-span-1 lg:col-span-2 h-full min-h-[300px]"
            style={{ backgroundColor: '#ab5af6' }}
          >
            <div className="max-w-full md:max-w-xs">
              <h3 className="text-left text-balance text-base md:text-xl lg:text-3xl font-semibold tracking-[-0.015em] text-white">
                {t.builtFor1Title}
              </h3>
              <p className="mt-4 text-left text-base/6 text-white/90">
                {t.builtFor1Desc}
              </p>
            </div>
            {/* Dashboard mockup - mobile: inline below text, desktop: absolute */}
            <div className="relative mt-6 mx-auto w-[260px] h-[180px] md:absolute md:mt-0 md:mx-0 md:-right-4 lg:-right-[15%] md:-bottom-8 md:w-[280px] lg:w-[380px] md:h-[200px] lg:h-[260px] rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-2xl overflow-hidden">
              <div className="bg-white/5 border-b border-white/10 px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-400/80" />
                <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
                <div className="w-2 h-2 rounded-full bg-green-400/80" />
                <span className="text-[9px] text-white/40 ml-2 font-mono">EZDocuAI Dashboard</span>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex gap-2">
                  <div className="w-16 h-5 rounded bg-purple-400/30" />
                  <div className="w-12 h-5 rounded bg-white/10" />
                  <div className="w-14 h-5 rounded bg-white/10" />
                </div>
                <div className="h-3 bg-white/10 rounded w-full" />
                <div className="h-3 bg-white/10 rounded w-4/5" />
                <div className="flex gap-2 mt-3">
                  <div className="flex-1 h-16 rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="h-2 w-8 bg-green-400/50 rounded mb-1" />
                    <div className="h-6 w-12 bg-white/20 rounded text-[8px] text-white/60 flex items-center justify-center font-bold">12</div>
                  </div>
                  <div className="flex-1 h-16 rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="h-2 w-8 bg-blue-400/50 rounded mb-1" />
                    <div className="h-6 w-12 bg-white/20 rounded text-[8px] text-white/60 flex items-center justify-center font-bold">8</div>
                  </div>
                  <div className="flex-1 h-16 rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="h-2 w-10 bg-purple-400/50 rounded mb-1" />
                    <div className="h-6 w-12 bg-white/20 rounded text-[8px] text-white/60 flex items-center justify-center font-bold">24</div>
                  </div>
                </div>
              </div>
            </div>
          </WobbleCard>

          {/* Card 2 - Narrow: Translation Agencies */}
          <WobbleCard
            containerClassName="col-span-1 min-h-[180px] md:min-h-[300px]"
            style={{ backgroundColor: '#ab5af6' }}
          >
            <h3 className="max-w-80 text-left text-balance text-base md:text-xl lg:text-3xl font-semibold tracking-[-0.015em] text-white">
              {t.builtFor2Title}
            </h3>
            <p className="mt-4 max-w-[26rem] text-left text-base/6 text-white/90">
              {t.builtFor2Desc}
            </p>
          </WobbleCard>

          {/* Card 3 - Full width: Immigration + Notaries with document flow mockup */}
          <WobbleCard
            containerClassName="col-span-1 lg:col-span-3 min-h-[300px] xl:min-h-[280px]"
            style={{ backgroundColor: '#ab5af6' }}
          >
            <div className="max-w-full md:max-w-sm">
              <h3 className="max-w-sm md:max-w-lg text-left text-balance text-base md:text-xl lg:text-3xl font-semibold tracking-[-0.015em] text-white">
                {t.builtFor3Title}, {t.builtFor4Title}
              </h3>
              <p className="mt-4 max-w-[26rem] text-left text-base/6 text-white/90">
                {t.builtFor3Desc} {t.builtFor4Desc}
              </p>
            </div>
            {/* Document flow mockup - mobile: inline below text, desktop: absolute centered */}
            <div className="relative mt-6 flex items-center justify-center gap-3 md:absolute md:mt-0 md:right-[4%] lg:right-[5%] md:top-1/2 md:-translate-y-1/2 md:w-[460px] lg:w-[680px] md:gap-5">
              {/* PDF doc */}
              <div className="w-[120px] h-[170px] md:w-[210px] md:h-[290px] rounded-xl md:rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-2xl p-3 md:p-5 rotate-[-3deg]">
                <div className="w-8 md:w-12 h-4 md:h-5 bg-red-400/60 rounded text-[6px] md:text-[8px] text-white font-bold flex items-center justify-center mb-2 md:mb-4">PDF</div>
                <div className="space-y-1.5 md:space-y-3">
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-full" />
                  <div className="h-1.5 md:h-2.5 bg-white/10 rounded w-4/5" />
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-full" />
                  <div className="h-1.5 md:h-2.5 bg-white/10 rounded w-3/4" />
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-[90%]" />
                  <div className="hidden md:block h-2.5 bg-white/10 rounded w-[65%]" />
                  <div className="hidden md:block h-2.5 bg-white/15 rounded w-[80%]" />
                </div>
                <div className="mt-3 md:mt-5 w-10 md:w-14 h-10 md:h-14 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <Award className="w-5 md:w-7 h-5 md:h-7 text-white/40" />
                </div>
              </div>
              {/* Arrow */}
              <div className="flex flex-col items-center">
                <ArrowRight className="w-5 md:w-8 h-5 md:h-8 text-white/40" />
              </div>
              {/* Translated doc */}
              <div className="w-[120px] h-[170px] md:w-[210px] md:h-[290px] rounded-xl md:rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-2xl p-3 md:p-5 rotate-[2deg]">
                <div className="flex gap-1 md:gap-2 mb-2 md:mb-4">
                  <div className="w-7 md:w-10 h-4 md:h-5 bg-green-400/60 rounded text-[5px] md:text-[7px] text-white font-bold flex items-center justify-center">EN</div>
                  <div className="w-4 md:w-5 h-4 md:h-5 bg-white/20 rounded flex items-center justify-center">
                    <CheckCircle className="w-2 md:w-3 h-2 md:h-3 text-green-400" />
                  </div>
                </div>
                <div className="space-y-1.5 md:space-y-3">
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-full" />
                  <div className="h-1.5 md:h-2.5 bg-white/10 rounded w-3/4" />
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-[85%]" />
                  <div className="h-1.5 md:h-2.5 bg-white/10 rounded w-full" />
                  <div className="h-1.5 md:h-2.5 bg-white/15 rounded w-[70%]" />
                  <div className="hidden md:block h-2.5 bg-white/10 rounded w-[80%]" />
                  <div className="hidden md:block h-2.5 bg-white/15 rounded w-[90%]" />
                </div>
                <div className="mt-3 md:mt-5 flex items-center gap-1 md:gap-2">
                  <div className="w-3 md:w-5 h-3 md:h-5 rounded-full bg-green-500/60" />
                  <span className="text-[6px] md:text-[8px] text-white/50 font-semibold">{t.uscisReady}</span>
                </div>
              </div>
            </div>
          </WobbleCard>
        </div>
      </div>
    </section>
  );
}
